import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, ChevronDown, ChevronUp, BookOpen, Info } from 'lucide-react';
import { useManual } from './ManualContext.shared';
import type { ManualSubSection } from '../../utils/manualData';

const ManualSubSectionItem = ({ section, idx }: { section: ManualSubSection, idx: number }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
        <div className="border-b border-border last:border-0">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between py-3 px-4 text-left hover:bg-background transition-colors"
            >
                <div className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                    </span>
                    <span className="font-medium text-content text-sm">{section.subtitlu}</span>
                </div>
                {isOpen ? <ChevronUp size={16} className="text-content-muted" /> : <ChevronDown size={16} className="text-content-muted" />}
            </button>

            {isOpen && (
                <div className="px-4 pb-4 animate-fadeIn">
                    <div className="ml-9 space-y-4">
                        {/* Description & Child Explanation */}
                        {section.descriere && (
                            <p className="text-sm text-content-secondary leading-relaxed">{section.descriere}</p>
                        )}
                        {section.explicatie_copil && (
                            <div className="bg-warning/10 rounded-lg p-3 border border-warning/30">
                                <p className="text-xs text-warning italic flex gap-2">
                                    <span className="not-italic">👶</span>
                                    {section.explicatie_copil}
                                </p>
                            </div>
                        )}

                        {/* Pasii Generali (Legacy/Simple) - Before complex sections */}
                        {section.pasi_generali && (
                            <ul className="space-y-2 mt-2">
                                {section.pasi_generali.map((pas: string, i: number) => (
                                    <li key={i} className="text-xs text-content-secondary flex items-start gap-2">
                                        <div className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0" />
                                        <span>{pas}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Metode Autentificare (Step 1) */}
                        {section.metode && (
                            <div className="grid gap-3 pt-2">
                                {section.metode.map((metoda, i: number) => (
                                    <div key={i} className="bg-surface border border-border rounded-lg p-3">
                                        <h5 className="font-bold text-sm text-content mb-2">{metoda.tip}</h5>
                                        {metoda.pasi && (
                                            <ul className="space-y-1 mb-2">
                                                {metoda.pasi.map((p: string, idx: number) => (
                                                    <li key={idx} className="text-xs text-content-secondary flex items-start gap-2">
                                                        <span className="text-content-muted select-none">•</span>
                                                        <span>{p}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {metoda.nota && (
                                            <p className="text-xs text-info bg-info/10 p-2 rounded italic">{metoda.nota}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Tipuri Abonament (Step 1) */}
                        {section.tipuri_abonament && (
                            <div className="space-y-3 pt-2">
                                {section.tipuri_abonament.map((sub, i: number) => (
                                    <div key={i} className="border border-border rounded-lg p-3 hover:border-brand-primary/30 transition-colors">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h5 className="font-bold text-sm text-content">{sub.denumire_afisata}</h5>
                                                <p className="text-xs text-content-muted">{sub.descriere}</p>
                                            </div>
                                            <span className="bg-surface-subtle text-content-secondary text-xs font-mono px-2 py-1 rounded">{sub.pret}</span>
                                        </div>
                                        {sub.functionalitati && (
                                            <ul className="space-y-1 mt-2">
                                                {sub.functionalitati.map((f: string, idx: number) => (
                                                    <li key={idx} className="text-xs text-success flex items-start gap-1.5">
                                                        <span className="font-bold">✓</span>
                                                        <span>{f}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Tipuri Cautare (Step 2 - CRITICAL) */}
                        {section.tipuri_cautare && (
                            <div className="space-y-4 pt-2">
                                {section.tipuri_cautare.map((tip, i: number) => (
                                    <div key={i} className={`border rounded-lg p-3 ${tip.culoare_card?.includes('Albastru') ? 'border-info/30 bg-info/10' : tip.culoare_card?.includes('Violet') ? 'border-ai/30 bg-ai/10' : 'border-success/30 bg-success/10'}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="font-bold text-sm">{tip.tip}</span>
                                        </div>
                                        <p className="text-xs text-content-secondary mb-3">{tip.descriere_simpla}</p>

                                        {tip.ce_poti_cauta && (
                                            <div className="mb-2">
                                                <p className="text-xs font-semibold text-content-secondary mb-1">Ce poți căuta:</p>
                                                <ul className="list-disc list-inside text-xs text-content-secondary pl-1">
                                                    {tip.ce_poti_cauta.map((item: string, idx: number) => (
                                                        <li key={idx}>{item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {tip.exemple_corecte && (
                                            <div className="mt-2 bg-surface p-2 rounded border border-border">
                                                <p className="text-xs font-semibold text-success mb-1">Exemple Corecte:</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {tip.exemple_corecte.map((ex: string, idx: number) => (
                                                        <span key={idx} className="text-[10px] bg-success/10 text-success px-1.5 py-0.5 rounded border border-success/30">{ex}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {tip.atentie_foarte_important && (
                                            <div className="mt-3 bg-danger/10 border border-danger/30 p-2 rounded">
                                                <p className="text-xs font-bold text-danger mb-1">⚠️ {tip.atentie_foarte_important.mesaj}</p>
                                                {tip.atentie_foarte_important.explicatie_pentru_copii && (
                                                    <ul className="list-disc list-inside text-[10px] text-danger/80 pl-1">
                                                        {tip.atentie_foarte_important.explicatie_pentru_copii.slice(0, 3).map((e: string, idx: number) => (
                                                            <li key={idx}>{e}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Rich Steps (Pasi Utilizare) */}
                        {section.pasi_utilizare && (
                            <div className="space-y-4 pt-2">
                                {section.pasi_utilizare.map((step, i: number) => (
                                    <div key={i} className="relative pl-5 border-l-2 border-brand-primary/20 bg-background/50 p-2 rounded-r-lg">
                                        <div className="absolute -left-[5px] top-3 w-2.5 h-2.5 rounded-full bg-brand-primary border-2 border-white"></div>
                                        <h5 className="text-sm font-bold text-content">{step.titlu}</h5>
                                        <p className="text-xs text-content-secondary mt-1 mb-2">{step.descriere}</p>

                                        {step.actiune && (
                                            <div className="text-xs bg-surface border border-border p-2 rounded text-content-secondary font-medium mb-1">
                                                👆 {step.actiune}
                                            </div>
                                        )}
                                        {step.explicatie_copil && (
                                            <p className="text-xs text-warning italic mt-1">💡 {step.explicatie_copil}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Standard Steps (Legacy) */}
                        {section.pasi_detaliat && !section.pasi_utilizare && (
                            <ul className="space-y-2">
                                {section.pasi_detaliat.map((pas: string, i: number) => (
                                    <li key={i} className="text-sm text-content-secondary flex items-start gap-2">
                                        <span className="mt-1.5 w-1 h-1 rounded-full bg-brand-secondary flex-shrink-0" />
                                        <span>{pas}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {section.pasi && !section.pasi_detaliat && !section.pasi_utilizare && (
                            <ul className="space-y-2">
                                {section.pasi.map((pas: string, i: number) => (
                                    <li key={i} className="text-sm text-content-secondary flex items-start gap-2">
                                        <span className="mt-1.5 w-1 h-1 rounded-full bg-brand-secondary flex-shrink-0" />
                                        <span>{pas}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Real World Cases (Cazuri) */}
                        {section.cazuri && (
                            <div className="grid gap-3 pt-2">
                                {section.cazuri.map((caz, i: number) => (
                                    <div key={i} className="bg-surface border border-border rounded-lg p-3 shadow-subtle">
                                        <span className="text-xs font-bold bg-info/10 text-info px-2 py-0.5 rounded-full">{caz.profil}</span>
                                        <div className="mt-2 text-xs text-content-secondary space-y-1">
                                            <p><strong>Rutina:</strong> {Object.values(caz.rutina_zilnica || {}).join(', ')}</p>
                                            <p className="italic text-content-muted">"{caz.feedback}"</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Benefits (Beneficii) */}
                        {section.beneficii_principale && (
                            <div className="grid gap-2 pt-2">
                                {section.beneficii_principale.map((b, i: number) => (
                                    <div key={i} className="flex gap-2 items-start text-xs text-content-secondary border border-transparent hover:border-success/30 hover:bg-success/10 p-1.5 rounded transition">
                                        <span className="text-success font-bold">✓</span>
                                        <div>
                                            <span className="font-bold">{typeof b === 'string' ? b : b.beneficiu}</span>
                                            {typeof b !== 'string' && b.explicatie && <p className="text-content-muted mt-0.5">{b.explicatie}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Zone Principale (Step 3.2) */}
                        {section.zone_principale && (
                            <div className="space-y-3 pt-2">
                                {section.zone_principale.map((zona, i: number) => (
                                    <div key={i} className="bg-background p-3 rounded border border-border">
                                        <h5 className="font-bold text-xs text-content uppercase tracking-wider mb-1">{zona.zona}</h5>
                                        {Array.isArray(zona.ce_contine) ? (
                                            <ul className="list-disc list-inside text-xs text-content-secondary">
                                                {zona.ce_contine.map((c: string, idx: number) => (
                                                    <li key={idx}>{c}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-xs text-content-secondary">{zona.ce_contine}</p>
                                        )}
                                        {zona.explicatie_butoane && (
                                            <div className="mt-2 grid grid-cols-2 gap-2">
                                                {Object.entries(zona.explicatie_butoane).map(([key, val]) => (
                                                    <div key={key} className="bg-surface p-1.5 rounded border border-border">
                                                        <span className="block text-[10px] font-bold text-content-secondary">{key.replace('_', ' ')}</span>
                                                        <span className="block text-[10px] text-content-muted leading-tight">{val}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Taburi Detaliate (Step 3.3 - Tab Explanations) */}
                        {section.taburi_detaliate && (
                            <div className="space-y-4 pt-2">
                                {section.taburi_detaliate.map((tab, i: number) => (
                                    <div key={i} className="relative pl-4 border-l-2 border-info/30">
                                        <h5 className="text-sm font-bold text-info">Tab {tab.numar}: {tab.nume_tab}</h5>
                                        <p className="text-xs text-info/80 italic mb-2">{tab.pentru_ce_e_util}</p>

                                        {/* Ce gasesti aici */}
                                        {tab.ce_gasesti_aici && Array.isArray(tab.ce_gasesti_aici) && (
                                            <div className="space-y-1.5 mb-2">
                                                {tab.ce_gasesti_aici.map((item, idx: number) => {
                                                    if (typeof item === 'string') return <p key={idx} className="text-xs text-content-secondary">• {item}</p>;
                                                    return (
                                                        <div key={idx} className="text-xs">
                                                            <span className="font-semibold text-content-secondary">{item.camp || item.subsectiune}:</span>
                                                            <span className="text-content-secondary ml-1">{item.explicatie || item.explicatie_tehnica}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {tab.beneficiu_pentru_utilizator && (
                                            <div className="bg-info/10 px-2 py-1.5 rounded text-[10px] text-info">
                                                <span className="font-bold">Beneficiu:</span> {tab.beneficiu_pentru_utilizator}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Butoane Detaliate (Step 3.4) */}
                        {section.butoane_detaliate && (
                            <div className="grid grid-cols-1 gap-2 pt-2">
                                {section.butoane_detaliate.map((btn, i: number) => (
                                    <div key={i} className="flex gap-3 items-start p-2 bg-surface border border-border rounded shadow-subtle">
                                        <div className="mt-1 p-1 bg-surface-subtle rounded text-content-muted text-xs font-bold">BTN</div>
                                        <div>
                                            <h5 className="text-xs font-bold text-content">{btn.buton}</h5>
                                            <ul className="mt-1 space-y-0.5">
                                                {(Array.isArray(btn.ce_face) ? btn.ce_face : btn.ce_face ? [btn.ce_face] : []).map((desc: string, idx: number) => (
                                                    <li key={idx} className="text-[10px] text-content-muted">• {desc}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Shortcuts & Erori Comune */}
                        {section.shortcut_exemple_rapide && (
                            <div className="bg-warning/10 p-3 rounded border border-warning/30 mt-2">
                                <h5 className="text-xs font-bold text-warning mb-1">⚡ Scurtături:</h5>
                                <ul className="list-disc list-inside text-xs text-warning/80">
                                    {section.shortcut_exemple_rapide.map((s: string, i: number) => (
                                        <li key={i}>{s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {section.erori || section.erori_comune ? (
                            <div className="space-y-2 mt-2">
                                <h5 className="text-xs font-bold text-danger uppercase tracking-widest">Erori Posibile</h5>
                                {(section.erori || section.erori_comune || []).map((err, i: number) => (
                                    <div key={i} className="bg-danger/10 p-2 rounded border border-danger/30 text-xs">
                                        <p className="font-bold text-danger">{err.mesaj}</p>
                                        <p className="text-danger mt-0.5">{Array.isArray(err.solutie) ? err.solutie[0] : err.solutie}</p>
                                    </div>
                                ))}
                            </div>
                        ) : null}


                        {/* Limite Acces (Step 2.5) */}
                        {section.limite_acces && (
                            <div className="grid gap-2 pt-2">
                                {section.limite_acces.map((limit, i: number) => (
                                    <div key={i} className="border border-border rounded p-2 text-xs hover:bg-background">
                                        <div className="flex justify-between">
                                            <span className="font-bold text-content">{limit.tip_cont}</span>
                                            <span className="text-content-muted">{limit.limite}</span>
                                        </div>
                                        <p className="text-info mt-1 italic">{limit.cum_obtin_mai_mult}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Functionalitate Speciala (Step 3) */}
                        {section.functionalitate_speciala && (
                            <div className="bg-info/10 border border-info/30 rounded-lg p-3 my-2">
                                <h5 className="font-bold text-sm text-info flex items-center gap-2">
                                    <span className="text-lg">✨</span> {section.functionalitate_speciala.nume}
                                </h5>
                                {section.functionalitate_speciala.cum_functioneaza && (
                                    <ul className="list-disc list-inside text-xs text-info/80 mt-2 space-y-1">
                                        {section.functionalitate_speciala.cum_functioneaza.map((step: string, idx: number) => (
                                            <li key={idx}>{step}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {/* Filtre Examen */}
                        {section.filtre_examen && (
                            <div className="bg-background p-3 rounded border border-border text-xs">
                                <h5 className="font-bold text-content mb-2">{section.filtre_examen.descriere}</h5>
                                <div className="space-y-2">
                                    {Object.entries(section.filtre_examen.optiuni || {}).map(([key, val]) => (
                                        <div key={key} className="flex gap-2">
                                            <span className="font-bold min-w-[50px]">{key}:</span>
                                            <span className="text-content-secondary">{val.ce_arata}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Note */}
                        {section.nota && (
                            <div className="bg-info/10 p-3 rounded-lg border border-info/30 flex gap-2 items-start mt-2">
                                <Info className="w-4 h-4 text-info flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-info">{section.nota}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const ManualDrawer: React.FC = () => {
    const { isOpen, closeManual, currentStep } = useManual();

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[9999]" onClose={closeManual}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                            <Transition.Child
                                as={Fragment}
                                enter="transform transition ease-in-out duration-300 sm:duration-500"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-300 sm:duration-500"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                                    <div className="flex h-full flex-col overflow-y-scroll bg-surface shadow-2xl">
                                        {/* Header */}
                                        <div className="bg-brand-primary px-4 py-6 sm:px-6 relative overflow-hidden">
                                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                                <BookOpen size={120} className="text-white transform rotate-12 translate-x-10 -translate-y-10" />
                                            </div>
                                            <div className="flex items-center justify-between relative z-10">
                                                <Dialog.Title className="text-lg font-semibold leading-6 text-white flex items-center gap-2">
                                                    <BookOpen size={20} />
                                                    Ghid Utilizare
                                                </Dialog.Title>
                                                <div className="ml-3 flex h-7 items-center">
                                                    <button
                                                        type="button"
                                                        className="relative rounded-md text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        onClick={closeManual}
                                                    >
                                                        <span className="absolute -inset-2.5" />
                                                        <span className="sr-only">Close panel</span>
                                                        <X className="h-6 w-6" aria-hidden="true" />
                                                    </button>
                                                </div>
                                            </div>
                                            {currentStep && (
                                                <div className="mt-4 relative z-10">
                                                    <p className="text-info font-medium text-sm mb-1 uppercase tracking-wider opacity-90">Secțiunea {currentStep.numar}</p>
                                                    <h2 className="text-2xl font-bold text-white tracking-tight">{currentStep.titlu}</h2>
                                                    <p className="text-white/80 text-sm mt-1 line-clamp-2">{currentStep.descriere}</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="relative flex-1 px-0 py-0 sm:px-0">
                                            {currentStep ? (
                                                <div className="flex flex-col h-full">
                                                    {currentStep.subsectiuni && (
                                                        <div className="bg-surface">
                                                            {currentStep.subsectiuni.map((section, idx) => (
                                                                <ManualSubSectionItem key={idx} section={section} idx={idx} />
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="p-6 mt-auto bg-background border-t border-border">
                                                        {/* Observations Section */}
                                                        {(currentStep.observatii || currentStep.observatii_generale || currentStep.observatii_finale) && (
                                                            <div className="mb-4">
                                                                <h4 className="text-xs font-bold text-content-muted uppercase tracking-widest mb-3">Observații</h4>
                                                                <ul className="space-y-2">
                                                                    {[
                                                                        ...(currentStep.observatii || []),
                                                                        ...(currentStep.observatii_generale || []),
                                                                        ...(currentStep.observatii_finale || [])
                                                                    ].map((obs, i) => (
                                                                        <li key={i} className="text-xs text-content-muted flex items-start gap-2">
                                                                            <Info size={12} className="mt-0.5 flex-shrink-0" />
                                                                            <span>{obs}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center h-full p-8 text-center text-content-muted">
                                                    <BookOpen size={48} className="mb-4 opacity-20" />
                                                    <p className="text-lg font-medium text-content-muted">Niciun ghid disponibil</p>
                                                    <p className="text-sm">Nu există informații specifice pentru această pagină.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default ManualDrawer;
